/**
 * SocketContext - WebSocket connection management for real-time updates
 * 
 * This context provides a Socket.IO connection to the backend for receiving
 * real-time inbox and chat updates without polling.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { config } from '../utils/config';

interface SocketContextType {
    socket: Socket | null;
    isConnected: boolean;
    joinInbox: (inboxId: string) => void;
    leaveInbox: (inboxId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
    socket: null,
    isConnected: false,
    joinInbox: () => { },
    leaveInbox: () => { },
});

export const useSocket = () => useContext(SocketContext);

interface SocketProviderProps {
    children: React.ReactNode;
}

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
    const [socket, setSocket] = useState<Socket | null>(null);
    const [isConnected, setIsConnected] = useState(false);
    const currentInboxRef = useRef<string | null>(null);

    useEffect(() => {
        const token = localStorage.getItem('auth_token');

        if (!token) {
            console.log('🔌 Socket: No auth token, skipping connection');
            return;
        }

        // Connect to WebSocket server
        const socketUrl = config.API_BASE_URL.replace('/api', '');
        console.log('🔌 Socket: Connecting to', socketUrl);

        const newSocket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            query: { token },
            autoConnect: true,
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
        });

        newSocket.on('connect', () => {
            console.log('🔌 Socket: Connected!');
            setIsConnected(true);
        });

        newSocket.on('disconnect', (reason) => {
            console.log('🔌 Socket: Disconnected -', reason);
            setIsConnected(false);
        });

        newSocket.on('connect_error', (error) => {
            console.error('🔌 Socket: Connection error -', error.message);
        });

        newSocket.on('connected', (data) => {
            console.log('🔌 Socket: Server confirmed connection:', data);
        });

        setSocket(newSocket);

        return () => {
            console.log('🔌 Socket: Cleaning up...');
            newSocket.close();
        };
    }, []);

    const joinInbox = useCallback((inboxId: string) => {
        if (socket && isConnected) {
            // Leave previous inbox room if any
            if (currentInboxRef.current && currentInboxRef.current !== inboxId) {
                socket.emit('leave_inbox', { inbox_id: currentInboxRef.current });
            }

            // Join new inbox room
            socket.emit('join_inbox', { inbox_id: inboxId });
            currentInboxRef.current = inboxId;
            console.log('🔌 Socket: Requested to join inbox:', inboxId);
        }
    }, [socket, isConnected]);

    const leaveInbox = useCallback((inboxId: string) => {
        if (socket && isConnected) {
            socket.emit('leave_inbox', { inbox_id: inboxId });
            if (currentInboxRef.current === inboxId) {
                currentInboxRef.current = null;
            }
            console.log('🔌 Socket: Requested to leave inbox:', inboxId);
        }
    }, [socket, isConnected]);

    return (
        <SocketContext.Provider value={{ socket, isConnected, joinInbox, leaveInbox }}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContext;
